import { useParams } from 'react-router-dom';
import { Box, Stack, Typography } from '@evaneos/b2b-ui';
import BudgetIcon from '@evaneos/b2b-ui/icons/LocalAtmOutlined';
import RouteIcon from '@evaneos/b2b-ui/icons/RouteOutlined';
import DirectionsIcon from '@evaneos/b2b-ui/icons/DirectionsOutlined';
import MemoIcon from '@evaneos/b2b-ui/icons/InsertDriveFileOutlined';
import NoteIcon from '@evaneos/b2b-ui/icons/NotesOutlined';
import SalesPotentialIcon from '@evaneos/b2b-ui/icons/CampaignOutlined';
import WarningIcon from '@evaneos/b2b-ui/icons/WarningAmber';
import { useIntl } from 'react-intl';

import { DefinitionList } from '../../../design-system/DefinitionList/DefinitionList';
import { DossierId } from '../../../resources/dossier/types';
import { useGetDossierData } from '../../../resources/dossier/queries';
import { RepeaterBadge } from '../../../design-system/RepeaterBadge/RepeaterBadge';
import { useDossierLayoutInfo } from '../DossierLayoutInfoContext';
import { SALES_PROCESS, LOST_STEP, CLOSED_STEP, CANCELLED_STEP } from '../../../domain';
import { DuplicateAlert } from '../components/alerts/DuplicateAlert';

import { MemoEditableItem } from './EditableItems/MemoEditableItem';
import { StepDefinitionEditableItem } from './EditableItems/StepDefinitionEditableItem';
import { TaskAndDueDateEditableItem } from './EditableItems/TaskAndDueDateEditableItem';
import { HeatLevelEditableItem } from './EditableItems/HeatLevelEditableItem';
import { BudgetEditableItem } from './EditableItems/BudgetEditableItem';
import { RequesterNameEditableItem } from './EditableItems/RequesterNameEditableItem';

// eslint-disable-next-line complexity -- dossier info display with multiple conditional sections
export const DossierMainInfo = () => {
    const { formatMessage } = useIntl();
    const { dossierId } = useParams() as { dossierId: DossierId };
    const { isFirstColumnNarrow } = useDossierLayoutInfo();

    const { dossier, memo, travellerInformation } = useGetDossierData(dossierId) || {};
    const { project, process, step, contract, task } = dossier || {};

    const shouldShowNextAction =
        step !== LOST_STEP && step !== CLOSED_STEP && step !== CANCELLED_STEP;

    const shouldWarnBudgetNotFilled =
        !dossier?.budget &&
        !project?.idealBudget &&
        contract?.state === 'not-issued' &&
        step !== LOST_STEP &&
        step !== CLOSED_STEP &&
        step !== CANCELLED_STEP;

    if (!dossier) {
        return null;
    }

    return (
        <Box
            sx={{
                borderBottom: '1px solid',
                borderColor: 'divider',
                marginBottom: 2,
                paddingBottom: 2,
            }}
        >
            <Stack gap={1}>
                <Stack gap={0.5}>
                    <Typography
                        sx={{ fontSize: '1.2rem', fontWeight: '600' }}
                        color="text.secondary"
                    >
                        {formatMessage(
                            { id: 'dossier.information.dossier_number' },
                            {
                                dossierId,
                            },
                        )}
                    </Typography>
                    <Stack direction="row" alignItems="center" gap={1} component="h2">
                        {travellerInformation?.isRepeater ? (
                            <RepeaterBadge size={20} noMargin />
                        ) : null}
                        <RequesterNameEditableItem
                            requesterName={dossier.project.requesterName}
                            requesterPhone={dossier.project.requesterPhone || ''}
                            dossierId={dossierId}
                        />
                    </Stack>
                </Stack>
                {dossier?.isDuplicate && dossier?.duplicateState !== 'ignored' && (
                    <DuplicateAlert />
                )}
                <DefinitionList>
                    <DefinitionList.Item>
                        <DefinitionList.Label
                            id="step-definition"
                            icon={<RouteIcon aria-hidden="true" fontSize="small" />}
                            iconOnly={isFirstColumnNarrow}
                        >
                            {formatMessage({ id: 'dossier.information.step_definition' })}
                        </DefinitionList.Label>
                        <StepDefinitionEditableItem dossierId={dossierId} />
                    </DefinitionList.Item>
                    {shouldShowNextAction && (
                        <>
                            <DefinitionList.Item>
                                <DefinitionList.Label
                                    id="next-action"
                                    icon={<DirectionsIcon aria-hidden="true" fontSize="small" />}
                                    iconOnly={isFirstColumnNarrow}
                                >
                                    {formatMessage({ id: 'dossier.information.next_action' })}
                                </DefinitionList.Label>
                                <TaskAndDueDateEditableItem
                                    key={task?.definition}
                                    dossierId={dossierId}
                                    task={task}
                                />
                            </DefinitionList.Item>
                            {task?.note ? (
                                <DefinitionList.Item>
                                    <DefinitionList.Label
                                        id="action-comment"
                                        icon={<NoteIcon aria-hidden="true" fontSize="small" />}
                                        iconOnly={isFirstColumnNarrow}
                                    >
                                        {formatMessage({ id: 'dossier.task.comment_label' })}
                                    </DefinitionList.Label>
                                    <DefinitionList.StaticValue>
                                        <Typography component="span" variant="body2">
                                            {task?.note}
                                        </Typography>
                                    </DefinitionList.StaticValue>
                                </DefinitionList.Item>
                            ) : null}
                        </>
                    )}
                    <DefinitionList.Item>
                        <DefinitionList.Label
                            id="budget"
                            icon={
                                shouldWarnBudgetNotFilled ? (
                                    <WarningIcon
                                        color="warning"
                                        aria-hidden="true"
                                        fontSize="small"
                                    />
                                ) : (
                                    <BudgetIcon aria-hidden="true" fontSize="small" />
                                )
                            }
                            iconOnly={isFirstColumnNarrow}
                        >
                            {formatMessage({
                                id: 'dossier.information.principal.budget.ideal.label.default',
                            })}
                        </DefinitionList.Label>
                        <BudgetEditableItem dossierId={dossierId} />
                    </DefinitionList.Item>
                    {process === SALES_PROCESS && (
                        <DefinitionList.Item>
                            <DefinitionList.Label
                                id="heat-level"
                                icon={<SalesPotentialIcon aria-hidden="true" fontSize="small" />}
                                iconOnly={isFirstColumnNarrow}
                            >
                                {formatMessage({
                                    id: 'dossier.closing_confidence.title',
                                })}
                            </DefinitionList.Label>
                            <HeatLevelEditableItem dossierId={dossierId} />
                        </DefinitionList.Item>
                    )}
                    <DefinitionList.Item>
                        <DefinitionList.Label
                            id="memo"
                            icon={<MemoIcon aria-hidden="true" fontSize="small" />}
                            iconOnly={isFirstColumnNarrow}
                        >
                            {formatMessage({ id: 'dossier.information.notes' })}
                        </DefinitionList.Label>
                        <MemoEditableItem
                            memo={memo ?? null}
                            dossierId={dossierId}
                            labelId="memo"
                        />
                    </DefinitionList.Item>
                </DefinitionList>
            </Stack>
        </Box>
    );
};
